import {
    ReactNode,
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
    selectActiveChargieId,
    selectHasPaymentMethod,
    selectRefreshToken,
    selectRequestedChargieId,
    setAccessToken,
    setActiveChargieId,
    setChargeAmp,
    setChargeCost,
    setChargeKw,
    setChargeKwh,
    setChargeStartTime,
    setChargeStatus,
    setDesiredChargieId,
    setDesiredConnector,
    setErrorType,
    setInactiveChargieId,
    setQueuedChargieId,
    setRequestedChargieId,
    setRetryChargieId,
} from '../appSlice';
import {
    ChargeStatus,
    Environment,
    RemoteErrorTypes,
    RemoteType,
    SecureStorageKeys,
    StartMethod,
} from '../utils/enums';
import { loader } from 'graphql.macro';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
    saveData,
    hideScanner,
    notifyLowSignalScan,
    notifyLowSignalQueue,
    queueRemote,
    open3dsModal,
    openExternalUrl,
    ENVIRONMENT_SWITCH_EVENT,
    PendingEnvironmentSwitchDetail,
} from '../utils/webview/messages';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    selectContactInformation,
    selectPaymentMethod,
    setContactInformation,
    setPaymentMethod,
} from '../guestSlice';
import { altClient } from '../utils/api';
import EnvironmentSwitchModal from './EnvironmentSwitchModal';

const guestRemoteStartMutation = loader(
    './PricingDetails/guestRemoteStart.graphql'
);
const remoteStartMutation = loader('../gql/remoteStart.graphql');
const loggedInUser = loader('../gql/getUserPayment.graphql');
const stationPricing = loader('../gql/getStationPricing.graphql');
const getStationRealmByQrCode = loader('../gql/getStationDetails.graphql');
const getGuestStationPricingQuery = loader(
    '../gql/getGuestStationPricing.graphql'
);
interface RemoteStartProviderProps {
    children: ReactNode;
}

interface RemoteStartContextType {
    chargieId: string | null | undefined;
}

interface PendingEnvironmentSwitch {
    environment: string;
    station: string;
    navigateTo: string;
}

const RemoteStartContext = createContext<RemoteStartContextType | undefined>(
    undefined
);

const CONNECTOR_SELECTOR_PATH = '/connectorSelector';

const RemoteStartProvider: React.FC<RemoteStartProviderProps> = ({
    children,
}) => {
    const dispatch = useAppDispatch();
    const [chargieId] = useState<string | null | undefined>(null);
    const requestedChargieId = useAppSelector(selectRequestedChargieId);
    const activeChargieId = useAppSelector(selectActiveChargieId);
    const hasPaymentMethod = useAppSelector(selectHasPaymentMethod);
    const navigate = useNavigate();
    const location = useLocation();
    const refreshToken = useAppSelector(selectRefreshToken);
    const [pendingEnvironmentSwitch, setPendingEnvironmentSwitch] =
        useState<PendingEnvironmentSwitch | null>(null);

    //guest checkout info
    const contactInformation = useAppSelector(selectContactInformation);
    const paymentMethod = useAppSelector(selectPaymentMethod);

    // Must be a ref — a plain `let` resets every render and can't debounce async work.
    const scannedRef = useRef(false);
    const awaitingConnectorRef = useRef<string | null>(null);
    const [remoteStart, { loading: remoteStartLoading }] =
        useMutation(remoteStartMutation);

    const [getUserDetails] = useLazyQuery(loggedInUser, {
        fetchPolicy: 'no-cache',
    });

    const [getStationPricing] = useLazyQuery(stationPricing, {
        fetchPolicy: 'no-cache',
    });
    const [getGuestStationPricing] = useLazyQuery(getGuestStationPricingQuery, {
        fetchPolicy: 'no-cache',
    });

    const [guestRemoteStart] = useMutation(guestRemoteStartMutation);

    useEffect(() => {
        const handleEnvironmentSwitch = (event: Event) => {
            const detail = (
                event as CustomEvent<PendingEnvironmentSwitchDetail>
            ).detail;
            if (!detail?.environment || !detail?.navigateTo) return;
            setPendingEnvironmentSwitch({
                environment: detail.environment,
                station: detail.station || '',
                navigateTo: detail.navigateTo,
            });
        };

        window.addEventListener(
            ENVIRONMENT_SWITCH_EVENT,
            handleEnvironmentSwitch
        );
        return () => {
            window.removeEventListener(
                ENVIRONMENT_SWITCH_EVENT,
                handleEnvironmentSwitch
            );
        };
    }, []);

    useEffect(() => {
        if (location.pathname !== CONNECTOR_SELECTOR_PATH) {
            awaitingConnectorRef.current = null;
        }
    }, [location.pathname]);

    useEffect(() => {
        const attemptCharge = async () => {
            if (
                remoteStartLoading ||
                !requestedChargieId ||
                scannedRef.current ||
                activeChargieId
            ) {
                return;
            }

            if (
                !requestedChargieId.connector &&
                awaitingConnectorRef.current === requestedChargieId.chargieId
            ) {
                dispatch(setRequestedChargieId(null));
                return;
            }

            scannedRef.current = true;
            const request = requestedChargieId;
            dispatch(setRequestedChargieId(null));
            hideScanner();
            try {
                if (refreshToken) {
                    const result = await getStationPricing({
                        variables: { qrCode: request.chargieId },
                    });

                    if (result.error) {
                        try {
                            const realmResult = await altClient.query({
                                query: getStationRealmByQrCode,
                                variables: { qrCode: request.chargieId },
                            });
                            if (realmResult.data?.getStationRealmByQrCode) {
                                hideScanner();
                                if (
                                    process.env.REACT_APP_ENVIRONMENT ===
                                    Environment.GOV.toLowerCase()
                                ) {
                                    openExternalUrl(
                                        `https://access.chargie.com/station/${request.chargieId}/charge/`
                                    );
                                } else {
                                    openExternalUrl(
                                        `https://access.fed.chargie.com/station/${request.chargieId}/charge/`
                                    );
                                }
                                dispatch(setActiveChargieId(null));
                                dispatch(setDesiredConnector(null));
                                return;
                            } else {
                                dispatch(
                                    setErrorType(
                                        RemoteErrorTypes.INVALID_STATION_CODE
                                    )
                                );
                                scannedRef.current = false;

                                dispatch(setActiveChargieId(null));
                                dispatch(setDesiredConnector(null));
                                dispatch(setRequestedChargieId(null));
                                navigate('/', { replace: true });
                                return;
                            }
                        } catch {
                            // Fall through and attempt start with whatever we have
                        }
                    }

                    const station =
                        result.data?.getStationPricing?.station ?? null;

                    if (station?.showConnectorSelector && !request.connector) {
                        scannedRef.current = false;
                        awaitingConnectorRef.current = request.chargieId;
                        navigate(CONNECTOR_SELECTOR_PATH, {
                            state: {
                                slide: true,
                                desiredId: request.chargieId,
                                mode: request.startType,
                                connectors: station.connectors,
                            },
                        });
                        return;
                    }

                    await startCharge(
                        request.chargieId,
                        request.startType,
                        request.connector || null,
                        station
                    );
                } else {
                    const result = await getGuestStationPricing({
                        variables: { qrCode: request.chargieId },
                    });
                    if (result.error) {
                        try {
                            const realmResult = await altClient.query({
                                query: getStationRealmByQrCode,
                                variables: { qrCode: request.chargieId },
                            });
                            if (realmResult.data?.getStationRealmByQrCode) {
                                hideScanner();
                                if (
                                    process.env.REACT_APP_ENVIRONMENT ===
                                    Environment.GOV.toLowerCase()
                                ) {
                                    setPendingEnvironmentSwitch({
                                        environment: Environment.PRODUCTION,
                                        station: request.chargieId,
                                        navigateTo: '/scan',
                                    });
                                } else {
                                    setPendingEnvironmentSwitch({
                                        environment: Environment.GOV,
                                        station: request.chargieId,
                                        navigateTo: '/scan',
                                    });
                                }
                                return;
                            }
                        } catch {
                            // Fall through
                        }
                    }

                    const otcDetails = result.data?.getOtcStationDetails;
                    if (otcDetails?.token) {
                        dispatch(setAccessToken(otcDetails.token));
                        saveData(
                            SecureStorageKeys.ACCESS_TOKEN,
                            otcDetails.token
                        );
                    }
                    if (
                        otcDetails?.station?.showConnectorSelector &&
                        !request.connector
                    ) {
                        scannedRef.current = false;
                        awaitingConnectorRef.current = request.chargieId;
                        navigate(CONNECTOR_SELECTOR_PATH, {
                            state: {
                                slide: true,
                                desiredId: request.chargieId,
                                mode: request.startType,
                                connectors: otcDetails.station.connectors,
                            },
                        });
                        return;
                    }
                    await startCharge(
                        request.chargieId,
                        request.startType,
                        request.connector || null,
                        otcDetails?.station ?? null
                    );
                }
            } catch {
                scannedRef.current = false;
            }
        };

        if (requestedChargieId) {
            attemptCharge();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedChargieId]);

    const startCharge = async (
        desiredId: string,
        methodType: StartMethod,
        connector: number | null,
        pricingStation?: {
            driverPricing?: any;
            inheritedPricingFrom?: any;
        } | null
    ) => {
        if (remoteStartLoading) return;
        dispatch(setInactiveChargieId(null));
        if (activeChargieId) {
            navigate('/session', { replace: true });
            return;
        } else if (!refreshToken) {
            if (paymentMethod && contactInformation) {
                try {
                    const response = await guestRemoteStart({
                        variables: {
                            input: {
                                phone: contactInformation,
                                qrCode: desiredId,
                                connector: connector,
                                paymentMethod: paymentMethod,
                                method: methodType || StartMethod.NATIVE_SCAN,
                            },
                        },
                    });
                    if (response.data?.guestRemoteStart) {
                        dispatch(setActiveChargieId(null));
                        dispatch(setDesiredConnector(null));
                        saveData(SecureStorageKeys.ACTIVE_CHARGIE_ID, '');
                        navigate('/guest/success', {
                            replace: true,
                            state: { slide: true },
                        });
                    }
                } catch (e) {
                    const { graphQLErrors } = e as any;

                    if (graphQLErrors && graphQLErrors.length > 0) {
                        if (graphQLErrors[0].errorType === 'RequiresAction') {
                            open3dsModal(graphQLErrors[0].message);
                        } else
                            dispatch(setErrorType(graphQLErrors[0].errorType));
                    }
                    navigate('/', { replace: true });
                } finally {
                    scannedRef.current = false;
                    dispatch(setDesiredChargieId(null));
                    dispatch(setRequestedChargieId(undefined));
                    dispatch(setPaymentMethod(null));
                    dispatch(setContactInformation(null));
                }
            } else {
                scannedRef.current = false;
                dispatch(
                    setDesiredChargieId({
                        chargieId: desiredId,
                        startType: methodType,
                        connector: connector,
                    })
                );
                dispatch(setRequestedChargieId(undefined));
                navigate('/guest/pricing', { state: { slide: true } });
            }
        } else {
            if (!hasPaymentMethod) {
                // Use station from the awaited pricing call — hook state is often
                // still stale on the first attempt and used to no-op here.
                if (!pricingStation) {
                    return;
                }

                const pricing =
                    pricingStation.driverPricing ||
                    pricingStation.inheritedPricingFrom;
                if (
                    pricing &&
                    pricing.rate !== 0 &&
                    !pricing.penaltyRate &&
                    pricing.policy !== 'TOU'
                ) {
                    const userDetails = await getUserDetails();
                    if (
                        !userDetails.data ||
                        !userDetails.data.loggedInUser ||
                        !userDetails.data.loggedInUser.isCardOnFile
                    ) {
                        dispatch(
                            setDesiredChargieId({
                                chargieId: desiredId,
                                startType: methodType,
                                connector: connector,
                            })
                        );
                        navigate('/pay');
                        return;
                    }
                }
            }
            try {
                const result = await remoteStart({
                    variables: {
                        qrCode: desiredId,
                        method: methodType,
                        connector: connector,
                    },
                });
                if (result?.data?.remoteStart) {
                    saveData(SecureStorageKeys.ACTIVE_CHARGIE_ID, desiredId);
                    dispatch(setErrorType(null));
                    dispatch(setRequestedChargieId(null));
                    dispatch(setActiveChargieId(desiredId));
                    dispatch(setDesiredConnector(connector));
                    hideScanner();
                    scannedRef.current = false;
                    navigate('/session', { replace: true });
                }
            } catch (e) {
                const { graphQLErrors } = e as any;
                dispatch(setRequestedChargieId(undefined));
                scannedRef.current = false;
                if (graphQLErrors && graphQLErrors.length === 0) {
                    notifyLowSignalScan(false);
                    dispatch(setQueuedChargieId(desiredId));
                    dispatch(setActiveChargieId(desiredId));
                    dispatch(setDesiredConnector(connector));
                    notifyLowSignalQueue(true);
                    queueRemote(desiredId, RemoteType.REMOTE_START);
                    dispatch(setChargeStatus(ChargeStatus.QUEUED));
                    dispatch(setChargeAmp(0));
                    dispatch(setChargeKwh(0));
                    dispatch(setChargeKw(0));
                    dispatch(setChargeCost(0));
                    dispatch(setChargeStartTime(null));
                    navigate('/session', { replace: true });
                    return;
                } else if (graphQLErrors && graphQLErrors.length > 0) {
                    dispatch(setActiveChargieId(null));
                    dispatch(setDesiredConnector(null));
                    dispatch(setRetryChargieId(desiredId));
                    if (graphQLErrors[0].errorType === 'RequiresAction') {
                        open3dsModal(graphQLErrors[0].message);
                    } else dispatch(setErrorType(graphQLErrors[0].errorType));
                } else {
                    dispatch(setActiveChargieId(null));
                    dispatch(setDesiredConnector(null));
                }
                navigate('/', { replace: true });
            }
        }
    };

    return (
        <RemoteStartContext.Provider value={{ chargieId }}>
            {children}
            {pendingEnvironmentSwitch && (
                <EnvironmentSwitchModal
                    environment={pendingEnvironmentSwitch.environment}
                    station={pendingEnvironmentSwitch.station}
                    navigateTo={pendingEnvironmentSwitch.navigateTo}
                />
            )}
        </RemoteStartContext.Provider>
    );
};

const useRemoteStart = (): RemoteStartContextType => {
    const context = useContext(RemoteStartContext);
    if (context === undefined) {
        throw new Error(
            'useRemoteStart must be used within a RemoteStartProvider'
        );
    }
    return context;
};

export { RemoteStartProvider, useRemoteStart };
